import React, { memo, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { Page } from 'react-pdf';
import Loader from '@components/Loader';
import PdfPageDisplayCanvas from '@pages/MediaViewer/AssetViewer/PdfCard/components/PdfPageDisplayCanvas';
import { hasRenderedTextLayerText } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';

const pageLoader = (
  <div className="media_viewer_pdf-mobile-loader">
    <Loader spinSize={40} isSpinning={true} />
  </div>
);

type PdfPageProps = {
  pageNumber: number;
  pageWidth?: number;
  pageHeight?: number;
  zoomFactor: number;
  turnoverDegree: number;
  isActive: boolean;
  isCanvasInteractive: boolean;
  isFirstPage: boolean;
  shouldRender: boolean;
  pageCanvasRef?: React.RefObject<HTMLCanvasElement>;
  onRegisterPageRoot: (page: number, element: HTMLDivElement | null) => void;
  onFirstPageLoadSuccess: (pdfPage: {
    originalWidth: number;
    originalHeight: number;
  }) => void;
  onPageLoadSuccess?: (pageNumber: number) => void;
  onPageTextContent?: (pageNumber: number, hasText: boolean) => void;
};

function PdfPage({
  pageNumber,
  pageWidth,
  pageHeight,
  zoomFactor,
  turnoverDegree,
  isActive,
  isCanvasInteractive,
  isFirstPage,
  shouldRender,
  pageCanvasRef,
  onRegisterPageRoot,
  onFirstPageLoadSuccess,
  onPageLoadSuccess,
  onPageTextContent
}: PdfPageProps) {
  const shouldRenderPage = shouldRender || isActive;
  const showDisplayCanvas =
    (!isActive || !isCanvasInteractive) && (shouldRender || isActive);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const assignRootRef = useCallback(
    (element: HTMLDivElement | null) => {
      rootRef.current = element;
      onRegisterPageRoot(pageNumber, element);
    },
    [onRegisterPageRoot, pageNumber]
  );

  return (
    <div
      ref={assignRootRef}
      className={classNames('media_viewer_pdf-mobile-page', {
        'b-active': isActive
      })}
      style={{ minHeight: pageHeight }}
    >
      <div
        className="media_viewer_pdf-inner"
        style={{
          transform: `rotate(${turnoverDegree}deg)`,
          width: pageWidth,
          height: pageHeight
        }}
      >
        {shouldRenderPage ? (
          <Page
            width={pageWidth}
            height={pageHeight}
            scale={1}
            pageNumber={pageNumber}
            className="media_viewer_pdf-page"
            canvasRef={isActive ? pageCanvasRef : undefined}
            renderAnnotationLayer={false}
            onLoadSuccess={(pdfPage) => {
              onPageLoadSuccess?.(pageNumber);
              if (isFirstPage) onFirstPageLoadSuccess(pdfPage);
            }}
            onRenderTextLayerSuccess={() => {
              onPageTextContent?.(
                pageNumber,
                hasRenderedTextLayerText(rootRef.current)
              );
            }}
            loading={pageLoader}
          />
        ) : null}
        <PdfPageDisplayCanvas
          pageNumber={pageNumber}
          pageWidth={pageWidth}
          pageHeight={pageHeight}
          zoomFactor={zoomFactor}
          turnoverDegree={turnoverDegree}
          visible={showDisplayCanvas}
          interactive={!isCanvasInteractive}
        />
      </div>
    </div>
  );
}

export default memo(PdfPage);
